/**
 * /el/guides/organosi-rsvp-gamou
 *
 * The Greek counterpart of `how-to-organize-wedding-rsvps.en.js` — what happens
 * to the answers once they start arriving, and the second half of the Greek
 * RSVP cluster.
 *
 * ── The adaptation ───────────────────────────────────────────────────────
 * The argument is the English article's and does not change: the hard part of
 * RSVPs is record-keeping, not asking. One central list, a current status on
 * every invitation, and people counted as PEOPLE rather than as accepted
 * invitations. That last one is the load-bearing idea of the whole piece.
 *
 * What is Greek rather than translated:
 *
 *  - **The channels.** The English list is form, text, email, phone, paper
 *    card. The Greek one is the reality: τηλέφωνο, Viber, μήνυμα, και — the
 *    case that actually breaks a list — μια θεία που μάζεψε τέσσερις
 *    απαντήσεις σε ένα οικογενειακό τραπέζι και σας τις είπε προφορικά.
 *  - **Το Excel** is named as the default, because in Greece it is. The
 *    article's honest position, which is the English one, is that there is no
 *    guest count at which a spreadsheet stops working.
 *  - **Οι στιγμές του γάμου** are μυστήριο and δεξίωση, which are genuinely two
 *    events with two attendance answers here, rather than a generic "moment".
 *
 * Two hedges are load-bearing and must survive editing: no guest count at which
 * a spreadsheet fails, and no universal review cadence. A couple whose
 * hand-kept list works is not doing it wrong.
 *
 * ── The claims this guide is allowed to make ─────────────────────────────
 * Four, all in the closing section:
 *
 *   - «Τα εργαλεία RSVP είναι διαθέσιμα στο πακέτο Heirloom.»
 *   - couples manage the guests they have invited
 *   - couples review the responses, with the people in each party
 *   - attendance is recorded per moment where the wedding has more than one
 *
 * And what it must never say: automatic reminders, automated follow-ups,
 * automatic messages to vendors, automatic submission of a final count,
 * automatic seating, personalised RSVP emails, a custom question builder, or
 * guest-entered dietary preferences. Dietary notes in Agapaé are entered by the
 * couple, which is why the article's one mention of them is advice about where
 * a detail should live and is never attributed to the product.
 *
 * No copy in this file contains "&", "<" or ">".
 */

const howToOrganizeWeddingRsvpsEl = {
  slug: "organosi-rsvp-gamou",

  title: "Πώς να οργανώσετε τα RSVP του γάμου χωρίς να χάσετε καλεσμένους",
  metaTitle: "Πώς να οργανώσετε τα RSVP του γάμου | Agapaé",
  metaDescription:
    "Πώς να κρατήσετε οργανωμένες τις απαντήσεις των καλεσμένων: μία λίστα, καθαρή κατάσταση σε κάθε πρόσκληση, συνοδοί, υπενθυμίσεις και τελικός αριθμός ατόμων.",

  eyebrow: "RSVP γάμου",

  hero: {
    src: "/assets/images/ceremony.jpeg",
    alt: "Ασπρόμαυρη φωτογραφία νύφης και γαμπρού που στέκονται αντικριστά σε τελετή στο ύπαιθρο, ανάμεσα σε δύο ψηλές ανθοστήλες, με τα σηκωμένα χέρια των καλεσμένων θολά στο πρώτο πλάνο",
    width: 1280,
    height: 714,
  },

  intro: [
    `Το να ζητήσετε απάντηση είναι το εύκολο κομμάτι. Το δύσκολο αρχίζει όταν οι απαντήσεις αρχίσουν να έρχονται.`,
    `Και σπάνια έρχονται από ένα σημείο. Άλλοι απαντούν στο προσκλητήριο, άλλοι με μήνυμα, άλλοι τηλεφωνικά, άλλοι σε ένα οικογενειακό τραπέζι — και κάποιος τρίτος σάς μεταφέρει τι είπαν.`,
    `Το πρόβλημα ξεκινά όταν αυτές οι απαντήσεις ζουν σε διαφορετικά μέρη και δεν υπάρχει πια ένας τρόπος να ξέρετε ποιους καλέσατε, ποιοι απάντησαν, ποιοι έρχονται, πόσα άτομα είναι και ποιους πρέπει ακόμη να ρωτήσετε.`,
    `Αυτός ο οδηγός είναι για το πώς κρατάτε τις απαντήσεις σε μια μορφή που μπορείτε πραγματικά να χρησιμοποιήσετε: μία λίστα, μία καθαρή κατάσταση ανά πρόσκληση και έναν αξιόπιστο τρόπο να καταλήξετε σε τελικό αριθμό.`,
  ],

  sections: [
    {
      heading: "Κρατήστε μία λίστα, και μόνο μία",
      content: [
        `Κρατήστε μία κεντρική λίστα καλεσμένων και δώστε σε κάθε πρόσκληση μια τρέχουσα κατάσταση μέσα σε αυτήν.`,
        `Η λίστα μπορεί να έχει σχεδόν οποιαδήποτε μορφή:`,
        {
          type: "list",
          items: [
            `ένα αρχείο Excel`,
            `ένα κοινό έγγραφο που ενημερώνετε και οι δύο`,
            `ένα σύστημα RSVP φτιαγμένο γι' αυτό`,
            `οποιαδήποτε άλλη οργανωμένη λίστα που θα κρατήσετε πραγματικά ενημερωμένη`,
          ],
        },
        `Η μορφή μετράει πολύ λιγότερο από τον κανόνα που τη συνοδεύει: υπάρχει μία λίστα, και αυτή είναι η σωστή.`,
        `Οι παράλληλες λίστες είναι που χαλούν τη δουλειά. Ο ένας κρατά ένα αρχείο, ο άλλος απαντά σε μηνύματα από το κινητό, η μαμά μαζεύει απαντήσεις στο τηλέφωνο, και στο τέλος κανείς δεν μπορεί να πει με σιγουριά πόσοι έρχονται.`,
      ],
    },
    {
      heading: "Ξεκινήστε από ολόκληρη τη λίστα, όχι από την πρώτη απάντηση",
      content: [
        `Η παρακολούθηση των απαντήσεων ξεκινά από τη λίστα των προσκεκλημένων, όχι από την πρώτη απάντηση που θα λάβετε.`,
        `Πριν έρθει οτιδήποτε, κάθε καλεσμένος ή κάθε παρέα πρέπει να υπάρχει ήδη ως εγγραφή. Αλλιώς η λίστα χτίζεται μία απάντηση τη φορά, και όσοι δεν απαντήσουν ποτέ δεν εμφανίζονται πουθενά.`,
        `Μια χρήσιμη εγγραφή κρατά συνήθως:`,
        {
          type: "list",
          items: [
            `το όνομα`,
            `το σπίτι ή την παρέα που καλύπτει η πρόσκληση`,
            `αν έχει σταλεί η πρόσκληση`,
            `την τρέχουσα κατάσταση της απάντησης`,
            `πόσα άτομα έρχονται`,
            `στοιχεία συνοδού, όπου υπάρχει`,
            `σημειώσεις που επηρεάζουν την οργάνωση`,
            `από ποιο κανάλι επικοινωνείτε μαζί τους`,
          ],
        },
        `Η λίστα καλεσμένων δεν είναι αρχείο όλων όσων γνωρίζετε. Κρατά ό,τι χρειάζεστε για να πάρετε αποφάσεις, και λίγα παραπάνω.`,
      ],
    },
    {
      heading: "Δώστε σε κάθε πρόσκληση καθαρή κατάσταση",
      content: [
        `Το απλούστερο μοντέλο που δουλεύει έχει τρεις καταστάσεις: εκκρεμεί, έρχεται, δεν έρχεται.`,
        `Κάθε πρόσκληση ξεκινά ως εκκρεμής, που σημαίνει μόνο ότι δεν έχει καταγραφεί ακόμη απάντηση.`,
        `Ο λόγος που υπάρχει η κατάσταση είναι η ορατότητα: οι αναπάντητες προσκλήσεις πρέπει να φαίνονται με μια ματιά και όχι να ανακαλύπτονται αργά.`,
        `Η μνήμη αρκεί για τις πρώτες δώδεκα απαντήσεις. Μετά, το να ψάχνετε σε μηνύματα μιας συζήτησης πριν από έξι εβδομάδες δεν είναι σύστημα.`,
        `Κάποια ζευγάρια προσθέτουν μια τέταρτη κατάσταση για όσους απάντησαν χωρίς να αποφασίσουν. Κρατήστε τις επιπλέον καταστάσεις λίγες και ξεκάθαρες, αλλιώς η κατάσταση παύει να σημαίνει κάτι.`,
      ],
    },
    {
      heading: "Μετρήστε ανθρώπους, όχι απαντήσεις",
      content: [
        `Μία πρόσκληση δεν είναι ένας καλεσμένος, και εδώ γίνεται το πιο συχνό λάθος.`,
        `Η Μαρία και ο Νίκος παίρνουν μία πρόσκληση. Η Μαρία απαντά και για τους δύο, οπότε ένα «ναι» σημαίνει δύο άτομα στη δεξίωση. Μια οικογενειακή πρόσκληση μπορεί να σημαίνει τέσσερα.`,
        `Μια λίστα που κρατά μόνο ναι και όχι σας λέει πόσες προσκλήσεις έγιναν δεκτές. Δεν σας λέει πόσες καρέκλες να βάλετε.`,
        `Κρατήστε λοιπόν τρία πράγματα ξεχωριστά:`,
        {
          type: "list",
          items: [
            `την πρόσκληση, δηλαδή αυτό που στείλατε`,
            `τα πρόσωπα που καλύπτει`,
            `τον αριθμό όσων έρχονται τελικά`,
          ],
        },
        `Το catering, η χωρητικότητα των τραπεζιών και το πλάνο δουλεύουν με ανθρώπους, όχι με προσκλήσεις.`,
      ],
    },
    {
      heading: "Ξεκαθαρίστε τους συνοδούς",
      content: [
        `Ένα «ναι» δεν λέει πάντα αρκετά.`,
        `Αν μια πρόσκληση περιλαμβάνει συνοδό, χρειάζεται να ξέρετε αν θα έρθει και, όπου έχει σημασία, ποιος είναι.`,
        `Πόση λεπτομέρεια θέλετε εξαρτάται από τον γάμο. Κάποιοι χρειάζονται μόνο τον αριθμό· άλλοι θέλουν και το όνομα, για τα καρτελάκια, για τα τραπέζια και για τις συστάσεις.`,
        `Κανένα από τα δύο δεν είναι πιο σωστό. Μαζέψτε ό,τι θα χρησιμοποιήσετε.`,
        `Η αρχή είναι η ίδια και στις δύο περιπτώσεις: πίσω από κάθε αριθμό υπάρχουν συγκεκριμένοι άνθρωποι.`,
      ],
    },
    {
      heading: "Κρατήστε χωριστά όσους έρχονται, όσους δεν έρχονται και όσους λείπουν",
      content: [
        `Οι τρεις ομάδες κάνουν διαφορετική δουλειά, οπότε δεν πρέπει να μπερδεύονται.`,
        {
          type: "list",
          items: [
            `Οι επιβεβαιωμένοι μετράνε στον αριθμό σας.`,
            `Όσοι αρνήθηκαν σταματούν να μετράνε, αλλά δεν εξαφανίζονται.`,
            `Όσοι δεν απάντησαν ανήκουν στη λίστα υπενθυμίσεων και σε κανένα από τα δύο σύνολα.`,
          ],
        },
        `Το να σβήσετε κάποιον που αρνήθηκε είναι δελεαστικό και συνήθως λάθος: χάνετε την πληροφορία ότι τον καλέσατε και ότι σας απάντησε.`,
        `Όσο οι τρεις ομάδες μένουν ξεχωριστές, η λίστα σας είναι έγγραφο οργάνωσης και όχι αρχείο μηνυμάτων.`,
      ],
    },
    {
      heading: "Οι λεπτομέρειες μένουν δίπλα στην απάντηση",
      content: [
        `Κάποιες απαντήσεις έρχονται με κάτι παραπάνω από ένα ναι. Ανάλογα με τον γάμο, μια απάντηση μπορεί να κουβαλά:`,
        {
          type: "list",
          items: [
            `επιλογή μενού, αν ζητάτε`,
            `διατροφικές ιδιαιτερότητες ή σημειώσεις, όπου τις μαζεύετε`,
            `στοιχεία συνοδού`,
            `παρουσία σε συγκεκριμένη στιγμή, όταν η πρόσκληση καλύπτει και το μυστήριο και τη δεξίωση`,
          ],
        },
        `Κανένας γάμος δεν χρειάζεται όλα αυτά, και το να μαζεύετε ό,τι δεν θα χρησιμοποιήσετε κάνει μόνο τη διαδικασία πιο βαριά.`,
        `Σημασία έχει πού καταλήγει η κάθε λεπτομέρεια: δίπλα στον άνθρωπο που την είπε, στην ίδια εγγραφή με την κατάστασή του.`,
        `Μια σημείωση για το φαγητό χαμένη μέσα σε μια συνομιλία είναι μια σημείωση που θα ψάχνετε την εβδομάδα που έχετε τον λιγότερο χρόνο.`,
      ],
    },
    {
      heading: "Φτιάξτε μια λίστα υπενθυμίσεων",
      content: [
        `Όσοι δεν έχουν απαντήσει πρέπει να γίνουν μια ορατή λίστα και όχι κάτι που προσπαθείτε να θυμάστε.`,
        `Μια πρακτική λίστα ξεχωρίζει λίγες καταστάσεις:`,
        {
          type: "list",
          items: [
            `δεν έχει απαντήσει`,
            `χρειάζεται υπενθύμιση`,
            `του μιλήσαμε`,
            `απάντησε`,
          ],
        },
        `Κάποιοι στέλνουν υπενθυμίσεις μαζεμένα, κάποιοι ζητούν από έναν γονιό να πάρει μερικά τηλέφωνα, κάποιοι προτιμούν να μιλήσουν οι ίδιοι σε όλους. Τίποτα από αυτά δεν χρειάζεται να είναι αυτοματοποιημένο για να δουλέψει.`,
        `Αυτό που σας δίνει η λίστα είναι το κομμάτι που δεν χωράει στο μυαλό: ποιος χρειάζεται προσοχή και γιατί.`,
        `Και αποτρέπει το πιο άβολο σενάριο — να ρωτήσετε τον ίδιο άνθρωπο τρεις φορές και κάποιον άλλον καμία.`,
      ],
    },
    {
      heading: "Όταν η απάντηση έρχεται από αλλού",
      content: [
        `Όσο καλά κι αν στηθεί το RSVP, κάποιες απαντήσεις θα έρθουν με άλλον τρόπο:`,
        {
          type: "list",
          items: [
            `ένα μήνυμα`,
            `ένα τηλεφώνημα`,
            `μια απάντηση σε email`,
            `ένας συγγενής που μεταφέρει τι είπαν τρεις άλλοι`,
            `μια κουβέντα σε άλλη εκδήλωση`,
          ],
        },
        `Είναι φυσιολογικό, και καλό είναι να το περιμένετε: ο καθένας απαντά από εκεί που του είναι πιο εύκολο.`,
        `Το πρόβλημα δεν είναι το κανάλι. Είναι η απάντηση που δεν έφτασε ποτέ στη λίστα.`,
        `Καταγράψτε τη μόλις μπορείτε ή μαζέψτε τις σκόρπιες απαντήσεις σε έναν τακτικό έλεγχο. Και τα δύο δουλεύουν, αρκεί η απάντηση να καταλήξει κάπου μόνιμα.`,
        `Και μόλις καταγραφεί, ο άνθρωπος δεν πρέπει να συνεχίζει να φαίνεται εκκρεμής επειδή δεν χρησιμοποίησε τη φόρμα. Αλλιώς θα περάσετε τον τελευταίο μήνα κυνηγώντας ανθρώπους που σας έχουν ήδη απαντήσει.`,
      ],
    },
    {
      heading: "Δείτε τη λίστα τακτικά",
      content: [
        `Μια λίστα είναι χρήσιμη όσο είναι ενημερωμένη, και οι απαντήσεις παλιώνουν αθόρυβα.`,
        `Ένας σύντομος έλεγχος κάνει τη μισή δουλειά:`,
        {
          type: "list",
          items: [
            `καταγράψτε τις νέες απαντήσεις`,
            `δείτε ποιοι μένουν εκκρεμείς`,
            `επιβεβαιώστε παρέες και συνοδούς`,
            `κοιτάξτε τον τρέχοντα αριθμό`,
            `ξεκαθαρίστε ό,τι είναι ασαφές όσο θυμάστε ακόμη τη συζήτηση`,
          ],
        },
        `Πόσο συχνά εξαρτάται από τον γάμο σας και από το πού βρίσκεστε: έχει πολύ μεγαλύτερη σημασία τις εβδομάδες γύρω από την προθεσμία παρά μήνες νωρίτερα.`,
        `Δεν χρειάζεται να παίρνει ώρα. Αρκεί να γίνεται αρκετά συχνά ώστε ο αριθμός στη λίστα σας να είναι αριθμός που θα δίνατε στο catering.`,
      ],
    },
    {
      heading: "Η προθεσμία δεν είναι ο τελικός αριθμός",
      content: [
        `Οι δύο ημερομηνίες σχετίζονται, αλλά δεν ταυτίζονται.`,
        `Η προθεσμία είναι η μέρα μέχρι την οποία ζητάτε απαντήσεις. Ο τελικός αριθμός είναι αυτό που δίνετε στον χώρο ή στο catering, στη δική τους προθεσμία.`,
        `Αν τις κάνετε μία, εξαφανίζετε ακριβώς τον χώρο μέσα στον οποίο γίνεται όλη η δουλειά αυτού του οδηγού: οι υπενθυμίσεις, οι διευκρινίσεις, οι επιβεβαιώσεις.`,
        {
          type: "p",
          text: [
            `Δείτε την προθεσμία ως τη στιγμή που αρχίζει να συγκροτείται ο τελικός αριθμός, όχι ως τη στιγμή που κλείνει. Το πώς επιλέγεται η ίδια η ημερομηνία είναι το θέμα του οδηγού `,
            {
              text: "Προθεσμία RSVP γάμου: πότε να ζητήσετε τις απαντήσεις",
              href: "/guides/when-should-wedding-guests-rsvp",
            },
            `.`,
          ],
        },
      ],
    },
    {
      heading: "Τι κάνετε με μια κλειστή λίστα",
      content: [
        `Μόλις η εικόνα γίνει αξιόπιστη, η λίστα αρχίζει να δουλεύει πραγματικά. Μια επιβεβαιωμένη λίστα στηρίζει:`,
        {
          type: "list",
          items: [
            `τον τελικό αριθμό ατόμων`,
            `την παρουσία στο μυστήριο και στη δεξίωση χωριστά`,
            `το φαγητό και το μενού`,
            `το πλάνο τραπεζιών`,
            `ό,τι άλλο εξαρτάται από το ποιοι θα είναι πραγματικά εκεί`,
          ],
        },
        `Τίποτα από αυτά δεν γίνεται μόνο του· τους συνεργάτες σας πρέπει να τους ενημερώσετε εσείς.`,
        `Και τα τραπέζια είναι εκεί όπου φαίνεται η ποιότητα των καταγραφών σας: κάθε τραπέζι εξαρτάται από το να ξέρετε ποιοι συγκεκριμένοι άνθρωποι έρχονται και ποιοι από αυτούς πάνε μαζί.`,
        {
          type: "p",
          text: [
            `Αν αυτό είναι το επόμενο βήμα σας, το `,
            {
              text: "Πλάνο τραπεζιών γάμου: πώς να το φτιάξετε βήμα βήμα",
              href: "/guides/wedding-seating-plan",
            },
            ` συνεχίζει από εκεί που τελειώνει αυτός ο οδηγός.`,
          ],
        },
      ],
    },
    {
      heading: "Excel ή εργαλείο RSVP;",
      content: [
        `Και τα δύο δουλεύουν, και η ειλικρινής απάντηση εξαρτάται από τον γάμο σας και όχι από το εργαλείο.`,
        `Το Excel είναι πραγματικά καλή επιλογή όταν:`,
        {
          type: "list",
          items: [
            `σας βολεύει να το συντηρείτε στο χέρι`,
            `οι καλεσμένοι είναι όσοι χωρούν σε μία οθόνη`,
            `οι απαντήσεις έρχονται από ένα δύο κανάλια`,
            `ενημερώνετε και οι δύο αξιόπιστα το ίδιο αρχείο`,
          ],
        },
        `Ένα σύστημα RSVP βοηθά περισσότερο όταν:`,
        {
          type: "list",
          items: [
            `οι καλεσμένοι είναι πολλοί`,
            `οι απαντήσεις έρχονται συχνά και από παντού`,
            `χρειάζεστε δομημένη πληροφορία και όχι ελεύθερο κείμενο, όπως το ποιοι ακριβώς είναι σε κάθε παρέα`,
            `η συντήρηση των λιστών έχει γίνει δουλειά από μόνη της`,
          ],
        },
        `Δεν υπάρχει αριθμός καλεσμένων πάνω από τον οποίο το Excel σταματά να δουλεύει.`,
        `Η χρήσιμη ερώτηση είναι πιο απλή: το σύστημά σας είναι ακόμη αξιόπιστο, και είναι ακόμη εύκολο να το κρατάτε ενημερωμένο;`,
      ],
    },
    {
      heading: "Η διαδικασία, σε δέκα βήματα",
      content: [
        `Όλη η δουλειά χωράει σε μια σελίδα:`,
        {
          type: "steps",
          items: [
            `Ξεκινήστε από την πλήρη λίστα των προσκεκλημένων.`,
            `Καταγράψτε πότε στάλθηκαν οι προσκλήσεις.`,
            `Δώστε σε κάθε πρόσκληση τρέχουσα κατάσταση.`,
            `Καταγράψτε τα πρόσωπα που έρχονται, όχι μόνο τα «ναι».`,
            `Ξεκαθαρίστε τους συνοδούς όπου υπάρχουν.`,
            `Κρατήστε τις σημειώσεις δίπλα στον άνθρωπο που αφορούν.`,
            `Ελέγξτε τις εκκρεμείς απαντήσεις πριν και γύρω από την προθεσμία.`,
            `Επικοινωνήστε με όσους δεν απάντησαν.`,
            `Κλείστε τη λίστα των επιβεβαιωμένων.`,
            `Χρησιμοποιήστε την για τον τελικό αριθμό και για το πλάνο τραπεζιών.`,
          ],
        },
        `Κάθε βήμα προστατεύει το επόμενο: παραλείψτε το τέταρτο και ο τελικός σας αριθμός είναι εικασία.`,
      ],
    },
    {
      heading: "Ζητούμενο είναι μια λίστα που εμπιστεύεστε",
      content: [
        `Το καλύτερο σύστημα δεν είναι το πιο σύνθετο.`,
        `Είναι αυτό που σας επιτρέπει να απαντήσετε, χωρίς να ανοίξετε τίποτε άλλο:`,
        {
          type: "list",
          items: [
            `ποιους καλέσατε`,
            `ποιοι απάντησαν`,
            `ποιοι έρχονται`,
            `πόσα άτομα είναι`,
            `ποιοι θέλουν ακόμη προσοχή`,
            `τι μένει ανοιχτό`,
          ],
        },
        `Αν το αρχείο σας απαντά και στα έξι, είναι καλό σύστημα. Αν έπαψε να απαντά, αξίζει να το φτιάξετε πριν από την προθεσμία.`,
        {
          type: "note",
          text: `Τα εργαλεία RSVP στο Agapaé είναι διαθέσιμα στο πακέτο Heirloom.`,
        },
        `Στο Agapaé οι καλεσμένοι και οι απαντήσεις τους βρίσκονται στο ίδιο σημείο. Κρατάτε τους ανθρώπους που καλέσατε και βλέπετε τις απαντήσεις καθώς έρχονται, μαζί με τα πρόσωπα κάθε παρέας και — όταν ο γάμος έχει χωριστές στιγμές, όπως το μυστήριο και τη δεξίωση — με το ποιος έρχεται σε ποια.`,
        {
          type: "p",
          text: [
            `Για το ίδιο το προϊόν, δείτε το `,
            { text: "RSVP γάμου", href: "/wedding-rsvp" },
            `.`,
          ],
        },
      ],
    },
  ],

  faqHeading: "Συχνές ερωτήσεις",

  faq: [
    {
      q: `Ποιος είναι ο καλύτερος τρόπος να κρατάω τις απαντήσεις;`,
      a: `Μία κεντρική λίστα, όπου κάθε πρόσκληση έχει τρέχουσα κατάσταση, και την ενημερώνετε από όπου κι αν ήρθε η απάντηση. Και ένα αρχείο Excel και ένα σύστημα RSVP το κάνουν καλά. Το πρόβλημα δεν είναι το εργαλείο· είναι οι τρεις μισές λίστες.`,
    },
    {
      q: `Τι πρέπει να κρατάει η λίστα;`,
      a: `Όσα χρειάζεστε για να αποφασίσετε: όνομα, την παρέα που καλύπτει η πρόσκληση, αν έχει σταλεί, την κατάσταση της απάντησης, πόσα άτομα έρχονται, στοιχεία συνοδού όπου υπάρχει, τις σημειώσεις που όντως χρησιμοποιείτε και από ποιο κανάλι επικοινωνείτε. Κάθε επιπλέον στήλη είναι κάτι ακόμη που πρέπει να ενημερώνετε.`,
    },
    {
      q: `Πώς παρακολουθώ όσους δεν έχουν απαντήσει;`,
      a: `Κρατήστε τους ως ορατή λίστα και σημειώστε τι έχει ήδη γίνει, ώστε να μη ρωτηθεί κανείς δύο φορές. Πριν επικοινωνήσετε, δείτε μήπως απάντησε κάποιος άλλος από το σπίτι του ή μήπως απάντησε τηλεφωνικά και δεν καταγράφηκε.`,
    },
    {
      q: `Οι συνοδοί μπαίνουν στη λίστα;`,
      a: `Ναι. Ο συνοδός είναι άνθρωπος που έρχεται, τρώει και χρειάζεται θέση, οπότε ανήκει στον αριθμό. Καταγράψτε ποιες προσκλήσεις περιλαμβάνουν συνοδό, αν θα έρθει και το όνομά του όπου το χρειάζεστε, για τα καρτελάκια ή για τα τραπέζια.`,
    },
    {
      q: `Τι κάνω όταν έχουν έρθει όλες οι απαντήσεις;`,
      a: `Ελέγξτε τη λίστα πριν τη χρησιμοποιήσετε. Επιβεβαιώστε τον αριθμό, ξεκαθαρίστε τις ασαφείς απαντήσεις, δείτε παρέες και συνοδούς και κλείστε με τον έναν ή τον άλλον τρόπο όσους δεν απάντησαν. Πάνω σε αυτή τη λίστα χτίζονται ο τελικός αριθμός και το πλάνο τραπεζιών.`,
    },
  ],

  related: {
    heading: "Πού ταιριάζει αυτό στο Agapaé",

    guides: ["when-should-wedding-guests-rsvp", "how-to-organize-wedding-guest-list"],

    product: [
      {
        href: "/wedding-rsvp",
        label: "RSVP γάμου",
        blurb:
          "Οι καλεσμένοι που καλείτε και οι απαντήσεις τους στο ίδιο σημείο, ανά άτομο και ανά στιγμή του γάμου. Διαθέσιμο στο πακέτο Heirloom.",
      },
    ],
  },
};

export default howToOrganizeWeddingRsvpsEl;
